import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader, Message, useToaster, Button } from 'rsuite';

import BasicPanel from '../../components/panels/basicPanel/basicPanel';
import BasicWrapper from '../../components/basicWrapper/basicWrapper';
import BasicInput from '../../components/inputs/basicInput/basicInput';
import BasicDropdown from '../../components/inputs/basicDropdown/basicDropdown';
import { useMessenger } from '../../context/messengerContext';
import { useLanguage } from '../../context/localeContext';
import { getProfile, updateProfile } from '../../api/user';

import './ProfileUpdatePage.css';
import { isIOSVersionHigherThan26Version } from '../../utils/getUserAgentParser';
import PhoneInputField from '../../components/inputs/phoneInput/phoneInput';

export default function ProfileUpdatePage() {
    const { t } = useTranslation('basic');
    const { userId, platform, user } = useMessenger();
    const { language, setLanguage } = useLanguage();
    const navigate = useNavigate();
    const toaster = useToaster();

    const [isLoading, setIsLoading] = useState(false);
    const [isProfileLoading, setIsProfileLoading] = useState(true);
    const [profileError, setProfileError] = useState(null);

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [middleName, setMiddleName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [username, setUsername] = useState('');
    const [lang, setLang] = useState(language || 'ru');

    const isIOS26Version = isIOSVersionHigherThan26Version();

    useEffect(() => {
        const loadProfile = async () => {
            setIsProfileLoading(true);
            setProfileError(null);
            try {
                const profile = await getProfile({ platform, userId });
                setFirstName(profile?.first_name || user?.firstName || '');
                setLastName(profile?.last_name || user?.lastName || '');
                setMiddleName(profile?.middle_name || '');
                setPhoneNumber(profile?.phone_number || '');
                setUsername(profile?.username || user?.username || '');
                setLang(language || profile?.lang || 'ru');
            } catch (err) {
                setProfileError(err?.message || t('profile_error'));
            } finally {
                setIsProfileLoading(false);
            }
        };
        loadProfile();
    }, [userId]);

    const langOptions = [
        { label: t('russian'), value: 'ru' },
        { label: t('english'), value: 'en' },
        { label: t('kazakh'), value: 'kz' },
    ];

    const handleSubmit = async () => {
        if (!firstName.trim() || !lastName.trim()) {
            toaster.push(
                <Message type='error' showIcon>
                    {t('not_all_required_fields_filled')}
                </Message>,
                { placement: 'topCenter', duration: 3000 },
            );
            return;
        }

        setIsLoading(true);
        try {
            await updateProfile({
                platform,
                userId,
                ...(!!lastName.trim() && {
                    lastName: lastName.trim(),
                }),
                ...(!!firstName.trim() && {
                    firstName: firstName.trim(),
                }),
                lang,
                ...(!!middleName.trim() && {
                    middleName: middleName.trim(),
                }),
                ...(!!username.trim() && {
                    username: username.trim(),
                }),
                ...(!!phoneNumber.trim() && {
                    phoneNumber: phoneNumber.trim(),
                }),
            });

            try {
                window.localStorage?.setItem('lang', lang);
            } catch (_) {
                // ignore
            }

            // Обновляем язык приложения сразу, без перезагрузки
            setLanguage?.(lang);

            toaster.push(
                <Message type='success' showIcon>
                    {t('profile_update_success')}
                </Message>,
                { placement: 'topCenter', duration: 3000 },
            );
            navigate(-1);
        } catch (err) {
            toaster.push(
                <Message type='error' showIcon>
                    {err?.message || t('profile_update_error')}
                </Message>,
                { placement: 'topCenter', duration: 3000 },
            );
        } finally {
            setIsLoading(false);
        }
    };

    if (isProfileLoading) {
        return (
            <div
                className={`loader-container ${isIOS26Version ? 'ios26_top-safe-area' : ''}`}
            >
                <Loader size='lg' />
            </div>
        );
    }

    if (profileError) {
        return (
            <div
                className={`profileUpdatePage ${isIOS26Version ? 'ios26_top-safe-area' : ''}`}
            >
                <BasicPanel>
                    <p className='profileUpdatePage_error'>{profileError}</p>
                </BasicPanel>
            </div>
        );
    }

    return (
        <div
            className={`stepsHeaderWrapper gradient_bg_header profileUpdatePage ${isIOS26Version ? 'ios26_top-safe-area' : ''}`}
        >
            <BasicWrapper>
                <div className='contractsList_header'>
                    <div className='stepHeaderHeader heading-h4'>
                        {t('profile_update_title')}
                        <img
                            id='stepsHeaderImage'
                            src='https://s3.timeweb.com/8a6e091b-publick/template_header.png'
                            alt=''
                        />
                    </div>
                </div>

                <div className='profileUpdatePage_form'>
                    <BasicInput
                        label={t('first_name')}
                        placeholder={t('first_name')}
                        value={firstName}
                        onChange={setFirstName}
                    />
                    <BasicInput
                        label={t('last_name')}
                        placeholder={t('last_name')}
                        value={lastName}
                        onChange={setLastName}
                    />
                    <BasicInput
                        label={t('middle_name')}
                        placeholder={t('middle_name')}
                        value={middleName}
                        onChange={setMiddleName}
                    />
                    <PhoneInputField
                        defaultCountry='ru'
                        placeholder={t('phone')}
                        value={phoneNumber}
                        onChange={setPhoneNumber}
                    />
                    {platform !== 'web' && (
                        <BasicInput
                            label={t('profile_username')}
                            placeholder={t('profile_username')}
                            value={username}
                            onChange={setUsername}
                        />
                    )}
                    <BasicDropdown
                        label={t('language')}
                        placeholder={t('select_language')}
                        data={langOptions}
                        value={lang}
                        onChange={setLang}
                    />

                    <div className='profileUpdatePage_actions'>
                        <Button
                            className='Btn paragraph-16M'
                            appearance='subtle'
                            onClick={() => navigate(-1)}
                            disabled={isLoading}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            className='Btn paragraph-16M'
                            appearance='primary'
                            loading={isLoading}
                            onClick={handleSubmit}
                        >
                            {t('save')}
                        </Button>
                    </div>
                </div>
            </BasicWrapper>
        </div>
    );
}
